import React from 'react';
import { motion } from 'framer-motion';
import { services } from '../data/mock';
import { Disc3, Users, Globe, Calendar, Palette, ArrowRight } from 'lucide-react';

const serviceIcons = {
  Production: Disc3,
  Management: Users,
  Distribution: Globe,
  Events: Calendar,
  Creative: Palette,
};

const ServicesSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12,
      },
    },
  };

  return (
    <section id="services" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="service-tag" style={{ backgroundColor: '#d987ff', color: '#151515' }}>
            What We Do
          </span>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-extrabold uppercase text-black mt-4 mb-6">
            Our Services
          </h2>
          <p className="text-dark-grey text-lg lg:text-xl max-w-2xl">
            From studio to stage, we provide comprehensive support to bring your musical vision to life.
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {services.map((service, index) => {
            const Icon = serviceIcons[service.category];
            return (
              <motion.div
                key={service.id}
                variants={itemVariants}
                className="group relative rounded-lg p-8 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                style={{ backgroundColor: service.color }}
              >
                {/* Category Tag */}
                <span className="inline-block bg-black/10 rounded-full px-3 py-1 text-xs font-mono uppercase tracking-widest text-black/70 mb-6">
                  {service.category}
                </span>

                {/* Icon */}
                <div className="w-14 h-14 rounded-full bg-black/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Icon className="w-7 h-7 text-black/80" />
                </div>

                {/* Content */}
                <h3 className="font-display text-xl font-bold text-black uppercase mb-4 leading-tight">
                  {service.title}
                </h3>
                <p className="text-black/70 text-base leading-relaxed mb-6">
                  {service.description}
                </p>

                {/* Arrow */}
                <div className="flex items-center gap-2 text-black/60 group-hover:text-black transition-colors">
                  <span className="font-mono text-xs uppercase tracking-widest">Learn More</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
